import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import Loader from 'react-loader-spinner';
import { FiSave } from 'react-icons/fi';
import { API_URLS } from '../constants/config';
import { useI18n } from '../utilities/i18n';
import { authorizedPost, fileToBase64, getApiErrorMessage } from '../utilities/stadiumsApi';
import { unwrapUser } from '../utilities/adminAccess';
import { useAuth } from '../utilities/auth';
import { extractUser, getUserPhotoUrl } from '../utilities/usersApi';
import {
  TIMEZONE_OFFSET_MAX,
  TIMEZONE_OFFSET_MIN,
  clampTimezoneOffset,
  getBrowserTimezoneOffsetMinutes,
  getStoredTimezoneOffset,
  persistTimezoneOffset,
} from '../utilities/timezoneOffset';
import AdminPhotoUpload from '../components/AdminPhotoUpload';
import StadiumCrumbs from '../components/StadiumCrumbs';
import '../styles/stadiumsManagement.css';
import '../styles/tournamentsManagement.css';

const emptyForm = {
  name: '',
  family: '',
  vk: '',
  timezoneOffset: '',
};

const resolveAccountImage = (userData, user) => (
  user?.image
  || userData?.image
  || userData?.data?.image
  || ''
);

const accountImageUrl = (image, rev) => {
  const url = getUserPhotoUrl(image);
  if (!url) return '';
  if (!rev) return url;
  return `${url}${url.includes('?') ? '&' : '?'}v=${rev}`;
};

export default function EditAccount() {
  const dispatch = useDispatch();
  const { t } = useI18n();
  const { userData, setUserData } = useAuth();
  const user = unwrapUser(userData);
  const accountImage = resolveAccountImage(userData, user);

  const [form, setForm] = useState(emptyForm);
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(() => (
    accountImageUrl(accountImage, userData?.image_rev || user?.image_rev)
  ));
  const [photoError, setPhotoError] = useState('');
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ text: '', type: '' });
  const [passwordForm, setPasswordForm] = useState({
    old_password: '',
    new_password: '',
    new_password_confirm: '',
  });
  const [passwordErrors, setPasswordErrors] = useState({});
  const [passwordSaving, setPasswordSaving] = useState(false);
  const [passwordMessage, setPasswordMessage] = useState({ text: '', type: '' });

  useEffect(() => {
    const offset = getStoredTimezoneOffset()
      ?? clampTimezoneOffset(user.timezoneOffset)
      ?? getBrowserTimezoneOffsetMinutes();
    setForm({
      name: user.name || '',
      family: user.family || '',
      vk: user.vk || '',
      timezoneOffset: offset == null ? '' : String(offset),
    });
    setPhotoFile(null);
    setPhotoError('');
    setPhotoPreview(accountImageUrl(
      resolveAccountImage(userData, user),
      userData?.image_rev || user?.image_rev
    ));
    setErrors({});
    setMessage({ text: '', type: '' });
  }, [user.id, accountImage, userData?.image_rev, user?.image_rev]);

  const updateField = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    if (errors[key]) setErrors((prev) => ({ ...prev, [key]: '' }));
  };

  const validate = () => {
    const nextErrors = {};
    if (!form.name.trim()) nextErrors.name = t('usrNameRequired');
    if (!form.family.trim()) nextErrors.family = t('usrFamilyRequired');
    if (form.timezoneOffset !== '' && clampTimezoneOffset(form.timezoneOffset) == null) {
      nextErrors.timezoneOffset = t('accTimezoneInvalid');
    }
    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) return;
    setSaving(true);
    setMessage({ text: '', type: '' });
    try {
      const payload = {
        name: form.name.trim(),
        family: form.family.trim(),
        vk: form.vk.trim(),
      };
      if (photoFile) payload.image = await fileToBase64(photoFile);

      const response = await authorizedPost(dispatch, API_URLS.editAdminAccount, payload);
      const saved = extractUser(response.data);
      const nextOffset = clampTimezoneOffset(form.timezoneOffset);
      const browserOffset = getBrowserTimezoneOffsetMinutes();
      persistTimezoneOffset(nextOffset != null && nextOffset !== browserOffset ? nextOffset : null);

      const nextImage = saved.image
        || (photoFile && user.id ? `/images/users/avatars/${user.id}.png` : '')
        || resolveAccountImage(userData, user)
        || '';
      const nextUser = {
        ...userData,
        ...user,
        name: saved.name || payload.name,
        family: saved.family || payload.family,
        vk: saved.vk != null ? saved.vk : payload.vk,
        image: nextImage,
        timezoneOffset: saved.timezoneOffset != null ? saved.timezoneOffset : nextOffset,
        image_rev: photoFile ? Date.now() : (userData?.image_rev || user?.image_rev),
      };
      setUserData(nextUser);
      setPhotoFile(null);
      setPhotoPreview(accountImageUrl(nextUser.image, nextUser.image_rev));
      setMessage({ text: t('accSaved'), type: 'success' });
    } catch (error) {
      setMessage({ text: getApiErrorMessage(error, t('stSaveError')), type: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const updatePasswordField = (key, value) => {
    setPasswordForm((prev) => ({ ...prev, [key]: value }));
    if (passwordErrors[key]) setPasswordErrors((prev) => ({ ...prev, [key]: '' }));
  };

  const validatePassword = () => {
    const next = {};
    if (!passwordForm.new_password || passwordForm.new_password.length < 8) {
      next.new_password = t('accPasswordShort');
    }
    if (passwordForm.new_password !== passwordForm.new_password_confirm) {
      next.new_password_confirm = t('accPasswordsMismatch');
    }
    setPasswordErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleChangePassword = async () => {
    if (!validatePassword()) return;
    setPasswordSaving(true);
    setPasswordMessage({ text: '', type: '' });
    try {
      const payload = {
        new_password: passwordForm.new_password,
        new_password_confirm: passwordForm.new_password_confirm,
      };
      if (passwordForm.old_password) payload.old_password = passwordForm.old_password;
      await authorizedPost(dispatch, API_URLS.adminAuthChangePassword, payload);
      setPasswordForm({ old_password: '', new_password: '', new_password_confirm: '' });
      setPasswordMessage({ text: t('accPasswordChanged'), type: 'success' });
    } catch (error) {
      setPasswordMessage({ text: getApiErrorMessage(error, t('accPasswordChangeError')), type: 'error' });
    } finally {
      setPasswordSaving(false);
    }
  };

  return (
    <div className="layoutWrapper st-layout">
      <div className="matchCenterWrapper st-page tr-curator-page">
        <div className="matchCenterHeader">
          <div className="st-header">
            <StadiumCrumbs items={[{ label: t('accTitle') }]} />
          </div>
        </div>

        <div className="st-scroll">
          <div className="st-content">
            {message.text ? <div className={`st-message ${message.type}`}>{message.text}</div> : null}

            <div className="st-block tr-curator-form">
              <div className="tr-curator-main">
                <div className="st-field tr-curator-photo">
                  <label>{t('stPhoto')}</label>
                  <AdminPhotoUpload
                    preview={photoPreview}
                    error={photoError}
                    disabled={saving}
                    onFileChange={(file, error, preview) => {
                      if (error) {
                        setPhotoError(error);
                        return;
                      }
                      setPhotoError('');
                      setPhotoFile(file);
                      setPhotoPreview(preview);
                    }}
                  />
                </div>

                <div className="st-form-grid tr-curator-fields">
                  <div className="st-field">
                    <label>{t('usrFamily')} <span className="st-required">*</span></label>
                    <input
                      className={`st-input ${errors.family ? 'error' : ''}`}
                      value={form.family}
                      onChange={(event) => updateField('family', event.target.value)}
                    />
                    {errors.family ? <span className="st-field-error">{errors.family}</span> : null}
                  </div>
                  <div className="st-field">
                    <label>{t('usrName')} <span className="st-required">*</span></label>
                    <input
                      className={`st-input ${errors.name ? 'error' : ''}`}
                      value={form.name}
                      onChange={(event) => updateField('name', event.target.value)}
                    />
                    {errors.name ? <span className="st-field-error">{errors.name}</span> : null}
                  </div>
                  <div className="st-field">
                    <label>{t('usrVk')}</label>
                    <input
                      className="st-input"
                      value={form.vk}
                      placeholder={t('trVkPh')}
                      onChange={(event) => updateField('vk', event.target.value)}
                    />
                    <small className="st-photo-hint">{t('accVkHint')}</small>
                  </div>
                  <div className="st-field">
                    <label>{t('accTimezoneOffset')}</label>
                    <input
                      className={`st-input ${errors.timezoneOffset ? 'error' : ''}`}
                      type="number"
                      min={TIMEZONE_OFFSET_MIN}
                      max={TIMEZONE_OFFSET_MAX}
                      value={form.timezoneOffset}
                      onChange={(event) => updateField('timezoneOffset', event.target.value)}
                    />
                    {errors.timezoneOffset ? <span className="st-field-error">{errors.timezoneOffset}</span> : null}
                    <small className="st-photo-hint">{t('accTimezoneHint')}</small>
                  </div>
                </div>
              </div>
            </div>

            <div className="st-block tr-curator-form" style={{ marginTop: 16 }}>
              <h3 className="st-block-title" style={{ margin: '0 0 12px', fontSize: 16 }}>{t('accChangePassword')}</h3>
              {passwordMessage.text ? (
                <div className={`st-message ${passwordMessage.type}`}>{passwordMessage.text}</div>
              ) : null}
              <div className="st-form-grid tr-curator-fields">
                <div className="st-field">
                  <label>{t('accOldPassword')}</label>
                  <input
                    className="st-input"
                    type="password"
                    autoComplete="current-password"
                    value={passwordForm.old_password}
                    onChange={(event) => updatePasswordField('old_password', event.target.value)}
                  />
                </div>
                <div className="st-field">
                  <label>{t('accNewPassword')} <span className="st-required">*</span></label>
                  <input
                    className={`st-input ${passwordErrors.new_password ? 'error' : ''}`}
                    type="password"
                    autoComplete="new-password"
                    value={passwordForm.new_password}
                    onChange={(event) => updatePasswordField('new_password', event.target.value)}
                  />
                  {passwordErrors.new_password ? (
                    <span className="st-field-error">{passwordErrors.new_password}</span>
                  ) : null}
                </div>
                <div className="st-field">
                  <label>{t('accConfirmPassword')} <span className="st-required">*</span></label>
                  <input
                    className={`st-input ${passwordErrors.new_password_confirm ? 'error' : ''}`}
                    type="password"
                    autoComplete="new-password"
                    value={passwordForm.new_password_confirm}
                    onChange={(event) => updatePasswordField('new_password_confirm', event.target.value)}
                  />
                  {passwordErrors.new_password_confirm ? (
                    <span className="st-field-error">{passwordErrors.new_password_confirm}</span>
                  ) : null}
                </div>
              </div>
              <div style={{ marginTop: 12 }}>
                <button
                  type="button"
                  className="st-btn st-btn-primary"
                  onClick={handleChangePassword}
                  disabled={passwordSaving}
                >
                  {passwordSaving ? <Loader type="Oval" color="#101510" height={18} width={18} /> : null}
                  {passwordSaving ? t('trLoading') : t('accChangePassword')}
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className="st-actions">
          <button
            type="button"
            className="st-btn st-btn-primary"
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? <Loader type="Oval" color="#101510" height={18} width={18} /> : <FiSave />}
            {saving ? t('trLoading') : t('stSaveChanges')}
          </button>
        </div>
      </div>
    </div>
  );
}
